import React, { useState } from "react";
import {
  Grid,
  FormControl,
  FormHelperText,
  Divider
} from "@material-ui/core";
import { Formik } from "formik";
import * as Yup from "yup";
import { SubscriptionArray } from "../../../../shared/services/dataService";
import PageLoadingComponent from "../../../../shared/components/page/pageLoadingComponent";
import { AuthContext } from "../../../../shared/store/authProvider";
import { ResultStatus, API_ENDPOINT } from "../../../../shared/types/enums";
import PageErrorComponent from "../../../../shared/components/page/pageErrorComponent";
import LayoutService from "../../../../shared/services/layoutService";
import ApiService from "../../../../shared/services/apiService";
import ToastService from "../../../../shared/services/toastService";
import OktaAuthService from "../../../../public/authentication/oktaAuthService";
import FTPFolderService from "../ftpFolderService";
import SectionComponent from "../../../../shared/components/sectionComponent";

class UploadFileToFTPComponent extends React.Component {
  static contextType = AuthContext;
  oSubscriptions = new SubscriptionArray();
  STORE = FTPFolderService;

  constructor(props) {
    super(props);
    // init state
    this.state = {
      sourceSystemData: [],
      selectedSourceId: null,
      selectedFile: null,
      fetchResult: ResultStatus.NOT_LOADED,
      uploading: false,
      result: null
    };
  }

  componentWillUnmount() {
    this.oSubscriptions.cancelAll();
  }

  componentDidMount() {
    this.setState({ fetchResult: ResultStatus.LOADING });
    this.fetchSourceSystems();
  }

  fetchSourceSystems = () => {
    this.oSubscriptions.cancelAll();
    this.setState({ fetchResult: ResultStatus.LOADING, sourceSystemData: [] });

    const pageNumber = 1;
    const rowsPerPage = 250;

    this.oSubscriptions.add(
      this.STORE.getSourceSystems(this.context.user.tenantId, pageNumber, rowsPerPage).subscribe(
        (response) => {
          this.setState({
            sourceSystemData: response.filter(s => s.isFtpSource === 1),
          }, () => {
            this.setState({ fetchResult: ResultStatus.LOADED });
          });
        },
        (error) => {
          this.setState({ fetchResult: ResultStatus.ERROR });
          console.log(error);
        }
      ));
  };

  getInitialValues() {
    return {
      sourceSystemId: null,
      selectedFile: null,
    };
  }

  validationSchema = Yup.object().shape({});
  getValidationSchema() {
    const MAX_FILE_SIZE = 27 * 1024 * 1024; //// 28 MB in bytes calculated as 28 * 1024 * 1024
    this.validationSchema = Yup.object().shape({
      sourceSystemId: Yup.number().required("Required").typeError('Must select a Source System ID'),
      selectedFile: Yup.mixed().required("Must select an Upload File under 27MB")
        .test(
          "fileFormat",
          "File rejected. Please submit a .csv file.",
          value => value && value.name.toUpperCase().endsWith(".CSV")
        )
        .test(
          "fileSize",
          "Error - File size exceeds 27 MB.",
          value => value && value.size <= MAX_FILE_SIZE
        )
    });
    return this.validationSchema;
  }

  handleSubmit = async (_formikProps) => {
    if (!_formikProps.isSubmitting && _formikProps.isValid) {
      await this.validationSchema.validate(_formikProps.values, { abortEarly: false })
        .then((x) => {

          // 1) set the status
          this.setState({ fetchResult: ResultStatus.SAVING });

          // 2) extract the formik values into a FormData object
          const formData = new FormData();
          formData.append('tenantId', this.context.user.tenantId);
          formData.append('sourceSystemId', _formikProps.values.sourceSystemId);
          formData.append('userId', this.context.user.userId);
          formData.append('file', _formikProps.values.selectedFile);

          // 3) determine the action and assign the appropriate props
          const actionVerb = "POST";
          const targetUrl = `/Ftp/UploadFile`;
          const headers = {
            Authorization: `Bearer ` + OktaAuthService.getAccessToken(),
          };

          // 4) save to Api and subscribe for the result
          ApiService.setOBS(
            actionVerb,
            API_ENDPOINT.CORE,
            targetUrl,
            formData,
            headers
          ).subscribe(
            (successResult) => {
              if (successResult) {
                ToastService.showSuccess(`${successResult.message}`);
              } else {
                ToastService.showError("An Error occured while Uploading the File");
                console.error("Error: falsey successResult while saving File Upload", successResult);
              }
              this.setState({ fetchResult: ResultStatus.LOADED });
            },
            (errorResult) => {
              ToastService.showError("An Error occured while Uploading the File");
              console.error("Error while saving File Upload", errorResult);
              this.setState({ fetchResult: ResultStatus.LOADED });
            }
          );
        })
        .catch((erroObj) => {
          erroObj.inner.forEach(err => { _formikProps.setFieldError(err.path, err.message); });
        });
    }
  }

  render() {
    const { classes } = this.props;
    //TODO : need toetup roles and permissions
    const canAndForUpload = true;

    switch (this.state.fetchResult) {
      case ResultStatus.NOT_LOADED:
      case ResultStatus.LOADING:
        return (<PageLoadingComponent classes={classes} label="Loading Source Systems" />);
      case ResultStatus.LOADED:
        return (<>
          <Formik initialValues={this.getInitialValues()} validationSchema={this.getValidationSchema()} validationSchemaOptions={{ showMultipleFieldErrors: true }}>
            {(fProps) => (
              <form>
                <SectionComponent classes={classes} enableEditJsx={
                  canAndForUpload ? LayoutService.getReadOnlyActionsSolo(this, false, () => { this.handleSubmit(fProps) })
                    : <></>
                } />

                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12}>
                    {
                      LayoutService.getSelectControl(
                        this.state.isReadOnly,
                        {
                          ...classes,
                          menuPaper: classes.customMenuPaper, // override just the paper class
                        },
                        fProps,
                        "sourceSystemId",
                        "Source System",
                        this.state.sourceSystemData,
                        "systemId",
                        "sourceLabel",
                        "90%",
                        false
                      )
                    }
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      style={{ minWidth: "90%" }}
                      className={classes.dialogControl}
                      error={
                        fProps.errors["selectedFile"] !== undefined &&
                        fProps.errors["selectedFile"] !== null
                      }
                    >
                      <FormHelperText>Source Container</FormHelperText>
                      <input id="selectedFile" name="selectedFile" type="file"
                        className={classes.input}
                        accept=".csv"
                        onChange={(event) => {
                          fProps.setFieldValue("selectedFile", event.currentTarget.files[0]);
                        }}
                      />
                      <Divider />
                      <FormHelperText>{fProps.errors["selectedFile"]}</FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </>);
      case ResultStatus.SAVING:
        return (<PageLoadingComponent classes={classes} label="Uploading File" />);
      case ResultStatus.ERROR:
      default:
        return (
          <PageErrorComponent label="Error Loading Source Systems" classes={classes} onRetry={() => { this.fetchData(true); }} />
        );
    }
  }
}

export default LayoutService.getHocComponenet(UploadFileToFTPComponent);
